import { pageTitle } from '@palaccount/config';
import { JOB_ROLE_SURFACE_KEYS } from '@palaccount/contracts';
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

import { useCreateJobRole, useJobRoles, useUpdateJobRole } from './api';
import { HrTabs } from './hr-tabs';

/**
 * The job-role management UI (owner ruling R11, BR-HR-01-05a..d — the spec
 * left its home [decide at build time]; it lives as its own tab). Add,
 * rename, deactivate, and point a role at the surfaces it unlocks. Managing
 * the LIST needs the configuration permission (admin.edit); a read-only
 * viewer still sees the list.
 *
 * BR-PERM-24 — "the two must be shown with different words on screen. Calling
 * both of them 'role' is what makes this go wrong." THIS is the screen that
 * carries the distinguishing word: the tab, the heading and the add button all
 * say **Job Role** (`hr.tabJobRoles`, `hr.jobRolesTitle`, `hr.addJobRole`),
 * and the hint says what a tick here does and does not do — makes somebody
 * selectable in dropdowns, grants nothing (BR-PERM-22/23). The security-role
 * side lives on ADM-05 and ADM-06 and never appears on an HR screen, so the
 * two words are never in the same place.
 *
 * The word on the OTHER side is fixed by the spec and is not this build's to
 * change: ADM-05 §10's own table names its column **Role**, and HR-01 §5b's
 * own card is **Roles & system access**. Read literally, BR-PERM-24's second
 * sentence and those two layouts contradict each other. The reading taken is
 * that a concrete screen layout beats a cross-cutting sentence about wording,
 * and that the rule's actual failure mode — a reader meeting both and assuming
 * they are one thing — is what "Job Role" everywhere on this side prevents.
 * The residual (a bare "Role" column on ADM-05, and identical "Role name"
 * field labels on the two edit forms) is reported, not silently fixed: the
 * spec fixes those strings itself.
 */
export function JobRolesPage() {
  const { t, i18n } = useTranslation();
  const ar = i18n.language === 'ar';
  const { data: jobRoles, isPending } = useJobRoles(true);
  const createRole = useCreateJobRole();
  const updateRole = useUpdateJobRole();

  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [surfaces, setSurfaces] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = pageTitle(t('hr.jobRolesTitle'));
  }, [t]);

  return (
    <div className="grid gap-4">
      <HrTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('hr.jobRolesTitle')}</h2>
        <Gated permission="admin.edit" reasonKey="perm.cannot.configureJobRoles">
          <Button onClick={() => setAdding(true)}>
            <Plus aria-hidden /> {t('hr.addJobRole')}
          </Button>
        </Gated>
      </div>
      <p className="text-sm text-muted-foreground">{t('hr.jobRolesHint')}</p>
      {error ? (
        <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {adding ? (
        <Card>
          <CardContent className="grid gap-3">
            <div className="grid gap-1">
              <Label htmlFor="role-name">{t('hr.jobRoleName')}</Label>
              <Input id="role-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <fieldset className="grid gap-2">
              <legend className="text-sm font-medium">{t('hr.unlocks')}</legend>
              {JOB_ROLE_SURFACE_KEYS.map((surface) => (
                <label key={surface} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={surfaces.includes(surface)}
                    onCheckedChange={(checked) =>
                      setSurfaces((prev) =>
                        checked === true ? [...prev, surface] : prev.filter((s) => s !== surface),
                      )
                    }
                  />
                  {t(`hr.surface.${surface}`)}
                </label>
              ))}
            </fieldset>
            <div className="flex gap-2">
              <Button
                disabled={name.trim() === ''}
                onClick={() => {
                  setError(null);
                  createRole.mutate(
                    { name, surfaces: surfaces as never },
                    {
                      onSuccess: () => {
                        setAdding(false);
                        setName('');
                        setSurfaces([]);
                      },
                      onError: (cause) =>
                        setError(cause instanceof Error ? cause.message : t('common.saveFailed')),
                    },
                  );
                }}
              >
                {t('common.save')}
              </Button>
              <Button variant="outline" onClick={() => setAdding(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {isPending ? (
        <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(jobRoles ?? []).map((role) => (
            <Card key={role.id} className={cn(!role.active && 'opacity-60')}>
              <CardContent className="grid gap-2">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-semibold">{ar ? role.nameAr : role.name}</h3>
                  <span className="text-xs text-muted-foreground">
                    {t('hr.holders', { count: role.holders })}
                  </span>
                </div>
                {role.surfaces.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {role.surfaces.map((surface) => (
                      <span key={surface} className="rounded-full bg-muted px-2 py-0.5 text-[11px]">
                        {t(`hr.surface.${surface}`)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">{t('hr.unlocksNothing')}</p>
                )}
                {role.consequence ? (
                  <p className="text-xs text-muted-foreground">{ar ? role.consequenceAr : role.consequence}</p>
                ) : null}
                <Gated permission="admin.edit" reasonKey="perm.cannot.configureJobRoles">
                  <div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setError(null);
                        updateRole.mutate(
                          { jobRoleId: role.id, body: { active: !role.active } },
                          {
                            onError: (cause) =>
                              setError(cause instanceof Error ? cause.message : t('common.saveFailed')),
                          },
                        );
                      }}
                    >
                      {role.active ? t('hr.deactivateRole') : t('hr.reactivateRole')}
                    </Button>
                  </div>
                </Gated>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
